import { ApiService } from "@/services/api.service";
import type { TRequestStatusReason, TRequestStatusReasonGroup } from "@/types/intake";

export interface TRequestReasonCreate {
  label: string;
}

export interface TRequestReasonUpdate {
  label?: string;
  is_active?: boolean;
}

/**
 * The workspace's disposition-reason lists, as the Settings editor manages them
 * (#4286). The reviewer's picker reads the same endpoint uncached through
 * `useRequestReasons`, so an admin's edits show up in an open decision dialog
 * without invalidation.
 */
export class RequestReasonService extends ApiService {
  /** `includeInactive` is what the editor needs and the picker must not have:
   *  a retired reason stays resolvable for history but is never offered for a
   *  new disposition. */
  async list(
    slug: string,
    group: TRequestStatusReasonGroup,
    { includeInactive = false } = {},
  ): Promise<TRequestStatusReason[]> {
    const params = new URLSearchParams({ group });
    if (includeInactive) params.set("include_inactive", "1");
    return this.get<TRequestStatusReason[]>(
      `/api/workspaces/${slug}/request-reasons/?${params.toString()}`,
    );
  }

  async create(
    slug: string,
    group: TRequestStatusReasonGroup,
    data: TRequestReasonCreate,
  ): Promise<TRequestStatusReason> {
    return this.post<TRequestStatusReason>(`/api/workspaces/${slug}/request-reasons/`, {
      ...data,
      group,
    });
  }

  async update(
    slug: string,
    _group: TRequestStatusReasonGroup,
    reasonId: string,
    data: TRequestReasonUpdate,
  ): Promise<TRequestStatusReason> {
    return this.patch<TRequestStatusReason>(
      `/api/workspaces/${slug}/request-reasons/${reasonId}/`,
      data,
    );
  }

  async remove(slug: string, _group: TRequestStatusReasonGroup, reasonId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/request-reasons/${reasonId}/`);
  }

  /** One request per drag. The collection PATCH renumbers server-side in a
   *  single bulk update — sending a PATCH per row would be N round trips and a
   *  half-ordered list if one failed. */
  async reorder(
    slug: string,
    group: TRequestStatusReasonGroup,
    orderedIds: string[],
  ): Promise<TRequestStatusReason[]> {
    return this.patch<TRequestStatusReason[]>(`/api/workspaces/${slug}/request-reasons/`, {
      group,
      order: orderedIds,
    });
  }
}
